import { Link } from "react-router-dom";
import { useGetPublicContestsQuery } from "../../features/api/appApi";

const panelClass = "home-card-strong p-6 sm:p-8 lg:p-10 mb-6 sm:mb-8";
const sectionTitleClass = "home-title mt-4 text-2xl font-bold sm:text-4xl text-[var(--text)]";
const bodyTextClass = "text-sm sm:text-base home-muted leading-relaxed";
const primaryButtonClass = "home-btn-primary px-6 py-2.5 text-sm font-semibold sm:px-8 sm:py-3 sm:text-base cursor-pointer";
const secondaryButtonClass = "home-btn-secondary px-6 py-2.5 text-sm font-medium sm:px-8 sm:py-3 sm:text-base cursor-pointer";

function formatDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("en-BD", { dateStyle: "long" }).format(date);
}

export default function ContestsListPage() {
  const { data, isLoading } = useGetPublicContestsQuery();
  const contests = data || [];

  return (
    <main className="home-shell min-h-screen pt-24 pb-12">
      <div className="home-container px-4 sm:px-6">
        <section className={panelClass}>
          <span className="section-kicker mb-3">GUBCPC CONTESTS</span>
          <h1 className={sectionTitleClass}>Contests open for registration</h1>
          <p className={`mt-4 ${bodyTextClass}`}>
            Browse currently published contests and register your team or yourself before the deadline.
          </p>
        </section>

        {isLoading ? (
          <div className="home-card p-8 text-center">
            <p className={bodyTextClass}>Loading contests...</p>
          </div>
        ) : contests.length === 0 ? (
          <div className="home-card p-8 text-center">
            <p className={bodyTextClass}>No contests are open for registration right now.</p>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {contests.map((contest) => (
              <ContestCard key={contest.id} contest={contest} />
            ))}
          </div>
        )}
      </div>
    </main>
  );
}

function ContestCard({ contest }) {
  const deadline = formatDate(contest.registrationDeadlineAt);
  const contestDate = formatDate(contest.contestDate);
  const isOpen = !!contest.isRegistrationOpen;

  return (
    <article className="home-card flex flex-col gap-4 p-6 transition-all duration-300 hover:border-[var(--brand)]">
      <div className="flex items-start justify-between gap-3">
        <span className="inline-flex w-fit rounded-full border border-[var(--border)] bg-[var(--surface-soft)] px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-[var(--text)]">
          {contest.type === "team"
            ? `Team ${contest.minTeamSize}–${contest.maxTeamSize}`
            : "Individual"}
        </span>
        <span
          className={`inline-flex w-fit rounded-full border px-3 py-1 text-xs font-semibold ${
            isOpen
              ? "border-emerald-500/35 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
              : "border-rose-500/35 bg-rose-500/10 text-rose-600 dark:text-rose-400"
          }`}
        >
          {isOpen ? "Open" : "Closed"}
        </span>
      </div>

      <div>
        <h2 className="text-xl font-bold tracking-tight text-[var(--text)] sm:text-2xl">
          {contest.title}
        </h2>
        {contest.description ? (
          <p className="mt-2 line-clamp-3 text-sm home-muted leading-relaxed">
            {contest.description}
          </p>
        ) : null}
      </div>

      <div className="mt-auto grid gap-1.5 text-xs home-muted">
        {contest.venue ? <p>Venue: {contest.venue}</p> : null}
        {contestDate ? <p>Contest date: {contestDate}</p> : null}
        {deadline ? <p>Registration closes {deadline}</p> : null}
      </div>

      <Link
        to={`/contests/${contest.slug}`}
        className={`mt-2 inline-flex items-center justify-center text-center ${
          isOpen ? primaryButtonClass : secondaryButtonClass
        }`}
      >
        {isOpen ? "Register Now" : "View Details"}
      </Link>
    </article>
  );
}
